import type { CampaignCreationTask } from "@adpilot/shared";
import { IconLock } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ErrorState } from "@/components/shared/error-state";
import { SmartCreationDraftReview } from "@/components/smart-creation/SmartCreationDraftReview";
import { useDelayedPending } from "@/hooks/use-delayed-pending";
import { getApiErrorMessage } from "@/lib/api-error";
import { previewSmartCreationProductHistory } from "@/lib/smart-creation-api";
import { cn } from "@/lib/utils";
import { hasValidPromotionWindow } from "./workspace/draft-rules";
import { ReadonlyPlanSummary } from "./workspace/ReadonlyPlanSummary";
import { SmartCreationStepContent } from "./workspace/SmartCreationStepContent";
import { useEditQueues } from "./workspace/use-edit-queues";
import { useExitGuard } from "./workspace/use-exit-guard";
import { usePlanIdentityReset } from "./workspace/use-plan-identity-reset";
import { usePlanSession } from "./workspace/use-plan-session";
import { usePreviewCommands } from "./workspace/use-preview-commands";
import { usePreviewRegeneration } from "./workspace/use-preview-regeneration";
import { useShopTransition } from "./workspace/use-shop-transition";
import { useStepCommands } from "./workspace/use-step-commands";
import { useStepInputs } from "./workspace/use-step-inputs";
import { useStructureEvaluation } from "./workspace/use-structure-evaluation";
import { useWorkspaceMutations } from "./workspace/use-workspace-mutations";
import {
  SubmissionPendingAlert,
  WorkspaceFooter,
  WorkspaceHeader,
} from "./workspace/WorkspaceChrome";
import {
  DiscardChangesDialog,
  PreviewRegenerationDialog,
  ReviewStrategyDialog,
} from "./workspace/WorkspaceDialogs";
import {
  SmartCreationProductTransitionSkeleton,
  SmartCreationSkeleton,
} from "./workspace/WorkspaceSkeletons";

export { claimSmartCreationActionLock } from "./workspace/plan-queries";

interface SmartCreationWorkspaceProps {
  open: boolean;
  planId: string;
  shopId: string;
  shopName: string;
  canEdit: boolean;
  shopChanging: boolean;
  onShopChange: (shopId: string) => Promise<void>;
  onClose: () => void | Promise<void>;
  onTaskAccepted?: (task: CampaignCreationTask) => void | Promise<void>;
}

export function SmartCreationWorkspace({
  open,
  planId,
  shopId,
  shopName,
  canEdit,
  shopChanging,
  onShopChange,
  onClose,
  onTaskAccepted,
}: SmartCreationWorkspaceProps) {
  const { t } = useTranslation("smartCreation");
  const session = usePlanSession({ open, planId, shopId, canEdit });
  const {
    plan,
    draft,
    displayedDraft,
    currentStep,
    planQuery,
    capabilitiesQuery,
    capabilitiesRequired,
    actionSerializing,
    contentBlocking,
    revisionConflict,
    strategySubviewDirty,
    strategyEditorEpoch,
  } = session;
  const regeneration = usePreviewRegeneration(session.identity, session.browserDraftRef);
  const stepInputs = useStepInputs(session);
  const { pendingShopId, shopTransitionMinimumElapsed, selectCreationShop } = useShopTransition({
    shopId,
    shopChanging,
    onShopChange,
    loadedShopId: plan?.shopId,
    draft,
    capabilities: capabilitiesQuery.data,
  });

  const historyProducts = draft?.products ?? [];
  const productHistoryQuery = useQuery({
    queryKey: [
      "smart-creation",
      "product-history",
      shopId,
      planId,
      historyProducts.map(({ sku, asin, variationGroupKey }) => ({ sku, asin, variationGroupKey })),
    ],
    queryFn: () => previewSmartCreationProductHistory(planId, shopId, draft!),
    enabled: open && currentStep === "objectives" && historyProducts.length > 0,
    staleTime: 60_000,
    retry: false,
  });

  const objectiveInputsIncomplete =
    currentStep === "objectives" &&
    (!draft?.objective || draft.targetAcos === null || draft.totalDailyBudget === null);
  const {
    structureEvaluationQuery,
    selectedStructureEvaluation,
    structureEvaluationBlocksContinue,
  } = useStructureEvaluation({ session, open, planId, canEdit });
  const contentScrollRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    void currentStep;
    if (contentScrollRef.current) contentScrollRef.current.scrollTop = 0;
  }, [currentStep]);

  useEditQueues({ session, open, canEdit });

  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [reviewStrategyEditing, setReviewStrategyEditing] = useState(false);
  usePlanIdentityReset(session.identity, () => {
    setCancelDialogOpen(false);
    setReviewStrategyEditing(false);
  });
  const exitGuard = useExitGuard({ session, planId, canEdit, onClose });
  const mutations = useWorkspaceMutations({
    session,
    stepInputs,
    exitGuard,
    planId,
    shopId,
    closeCancelDialog: () => setCancelDialogOpen(false),
    onClose,
    onTaskAccepted,
  });
  const { publicationPending } = mutations;
  const actionPending = actionSerializing;
  const actionFeedbackPending = useDelayedPending(actionPending);
  const stepActionsPending =
    currentStep === "strategy" && !contentBlocking ? actionFeedbackPending : actionPending;
  const { validationError } = stepInputs;
  const { durableActionError } = session;
  useEffect(() => {
    if (pendingShopId) return;
    if (validationError) {
      toast.error(validationError);
      return;
    }
    if (!durableActionError) return;
    let active = true;
    void getApiErrorMessage(durableActionError).then((message) => {
      if (active) toast.error(message);
    });
    return () => {
      active = false;
    };
  }, [validationError, durableActionError, pendingShopId]);

  const steps = useStepCommands({
    session,
    stepInputs,
    mutations,
    canEdit,
    selectedStructureEvaluation,
  });
  const preview = usePreviewCommands({
    session,
    mutations,
    confirmPreviewRegeneration: regeneration.confirmPreviewRegeneration,
    goBack: steps.goBack,
  });

  const requiredDataUnavailable =
    canEdit &&
    (planQuery.isLoading ||
      planQuery.isError ||
      !plan ||
      !draft ||
      (capabilitiesRequired &&
        (capabilitiesQuery.isLoading || capabilitiesQuery.isError || !capabilitiesQuery.data)));
  const shopTransitionHasError = Boolean(
    pendingShopId && (planQuery.isError || (capabilitiesRequired && capabilitiesQuery.isError)),
  );
  const shopTransitionErrorVisible = shopTransitionHasError && shopTransitionMinimumElapsed;
  const shopTransitionShellVisible = Boolean(
    pendingShopId &&
      !shopTransitionErrorVisible &&
      (shopChanging ||
        plan?.shopId !== pendingShopId ||
        !draft ||
        planQuery.isLoading ||
        (capabilitiesRequired && !capabilitiesQuery.data)),
  );
  const reviewing = currentStep === "preview" && Boolean(plan?.reviewWorkspace);

  return (
    <>
      <div
        data-testid="smart-creation-workspace"
        data-smart-creation-flow
        className="flex min-h-0 w-full min-w-0 flex-1 flex-col overflow-hidden bg-surface has-[[data-strategy-subview]]:[&_[data-smart-creation-step-actions]]:hidden has-[[data-strategy-subview]]:[&>main]:p-0"
      >
        <WorkspaceHeader
          shopName={shopName}
          currentStepIndex={steps.currentStepIndex}
          reviewing={reviewing}
        />

        {publicationPending && !mutations.confirmMutation.isPending ? (
          <SubmissionPendingAlert
            actionPending={actionPending}
            discarding={mutations.discardSubmissionMutation.isPending}
            onDiscard={preview.discardPendingSubmission}
            onRetry={() => void preview.confirmReview()}
          />
        ) : null}

        <main
          ref={contentScrollRef}
          className={cn(
            "min-h-0 flex-1 bg-surface paper-scrollbar",
            reviewing ? "flex flex-col overflow-hidden" : "overflow-y-auto",
            reviewing ? "p-0" : "px-6 py-6",
          )}
        >
          {!canEdit ? (
            <p className="mb-4 flex items-center gap-2 text-xs text-muted-foreground">
              <IconLock className="size-4" />
              {t("permissions")}
            </p>
          ) : null}
          {shopTransitionShellVisible && pendingShopId ? (
            <SmartCreationProductTransitionSkeleton shopId={pendingShopId} />
          ) : null}
          {shopTransitionErrorVisible ? (
            <ErrorState
              message={t("errors.load")}
              onRetry={() => {
                void planQuery.refetch();
                void capabilitiesQuery.refetch();
              }}
            />
          ) : null}
          {!pendingShopId && planQuery.isLoading ? <SmartCreationSkeleton /> : null}
          {!pendingShopId && planQuery.isError ? (
            <ErrorState message={t("errors.load")} onRetry={() => void planQuery.refetch()} />
          ) : null}
          {plan && !canEdit ? <ReadonlyPlanSummary plan={plan} /> : null}
          {plan &&
          displayedDraft &&
          canEdit &&
          currentStep === "preview" &&
          plan.reviewWorkspace ? (
            <SmartCreationDraftReview
              key={`${plan.id}:${strategyEditorEpoch}`}
              plan={plan}
              workspace={plan.reviewWorkspace}
              creationCapabilities={capabilitiesQuery.data}
              disabled={
                actionPending || publicationPending || revisionConflict || strategySubviewDirty
              }
              refreshingPreview={mutations.refreshPreviewReviewMutation.isPending}
              confirming={mutations.confirmMutation.isPending}
              onApplyChanges={preview.applyReviewChanges}
              onApplyOptimistic={preview.applyOptimisticReviewChanges}
              onRefreshPreview={preview.refreshPreviewReview}
              onConfirm={preview.confirmReview}
              onBack={preview.backFromReview}
              onDirtyChange={session.handleReviewDirtyChange}
              onRetryPreparation={preview.retryPreviewPreparation}
              onEditStrategy={() => setReviewStrategyEditing(true)}
              onRemoveStrategyLane={(lane) => preview.removeStrategyLane(lane, plan.revision)}
            />
          ) : null}
          {plan &&
          displayedDraft &&
          canEdit &&
          currentStep !== "preview" &&
          !shopTransitionShellVisible &&
          !shopTransitionErrorVisible &&
          (!capabilitiesRequired || capabilitiesQuery.data) ? (
            <fieldset
              disabled={contentBlocking || publicationPending || revisionConflict}
              aria-busy={contentBlocking}
              className={cn(
                "min-w-0 border-0 p-0",
                (contentBlocking || revisionConflict) &&
                  "pointer-events-none select-none opacity-70",
              )}
            >
              <SmartCreationStepContent
                plan={plan}
                draft={displayedDraft}
                mutationPending={actionSerializing}
                objectiveRecommendation={stepInputs.objectiveRecommendation}
                productHistory={productHistoryQuery.data ?? null}
                productHistoryPending={productHistoryQuery.isLoading}
                productHistoryError={productHistoryQuery.isError}
                onRefreshProductHistory={() => void productHistoryQuery.refetch()}
                shopId={plan.shopId}
                shopChanging={shopChanging}
                pendingShopId={pendingShopId}
                shopTransitionMinimumElapsed={shopTransitionMinimumElapsed}
                onShopChange={selectCreationShop}
                capabilities={capabilitiesQuery.data}
                enterInput={stepInputs.enterInput}
                invalidProducts={stepInputs.invalidProducts}
                onInvalidProductsChange={stepInputs.setInvalidProducts}
                onCatalogBlockingChange={stepInputs.setCatalogBlocked}
                onDraftChange={(nextDraft, preserveRecommendation) => {
                  stepInputs.setValidationError(null);
                  if (hasValidPromotionWindow(nextDraft))
                    stepInputs.setPromotionValidationRequested(false);
                  stepInputs.changeLocalDraft(nextDraft, preserveRecommendation);
                }}
                onEnterInputChange={(value) => {
                  if (!session.actionLock.current && !revisionConflict)
                    stepInputs.setEnterInput(value);
                }}
                onReconnect={steps.reconnectAmazon}
                recommendationPending={mutations.recommendationMutation.isPending}
                promotionValidationRequested={stepInputs.promotionValidationRequested}
                onRefreshRecommendation={steps.refreshRecommendation}
                onApplyRecommendation={steps.applyRecommendation}
                strategyRecommendationPending={mutations.strategyRecommendationMutation.isPending}
                onApplyStrategyOperations={preview.applyStrategyOperations}
                onRefreshStrategyRecommendation={preview.refreshStrategyRecommendation}
                onAcceptStrategyRecommendation={preview.acceptStrategyRecommendation}
                strategyEditorEpoch={strategyEditorEpoch}
                onStrategyLocalDirtyChange={session.handleStrategySubviewDirtyChange}
                structureEvaluation={structureEvaluationQuery.data}
                structureEvaluationLoading={structureEvaluationQuery.isFetching}
                structureEvaluationError={structureEvaluationQuery.isError}
                onRetryStructureEvaluation={() => void structureEvaluationQuery.refetch()}
                onStructureChange={steps.selectStructure}
                onStructureRepair={steps.repairStructure}
              />
            </fieldset>
          ) : null}
          {!pendingShopId && capabilitiesRequired && capabilitiesQuery.isLoading ? (
            <SmartCreationSkeleton />
          ) : null}
          {!pendingShopId && capabilitiesRequired && capabilitiesQuery.isError ? (
            <ErrorState
              message={t("errors.load")}
              onRetry={() => void capabilitiesQuery.refetch()}
            />
          ) : null}
        </main>

        {!reviewing ? (
          <WorkspaceFooter
            canEdit={canEdit}
            currentStep={currentStep}
            currentStepIndex={steps.currentStepIndex}
            showCancel={Boolean(
              canEdit &&
                plan &&
                (plan.status === "draft" || plan.status === "preview_ready") &&
                !publicationPending,
            )}
            cancelDialogOpen={cancelDialogOpen}
            onCancelDialogOpenChange={(nextOpen) => {
              if (!mutations.cancelMutation.isPending) setCancelDialogOpen(nextOpen);
            }}
            onKeepEditing={() => setCancelDialogOpen(false)}
            cancelPending={mutations.cancelMutation.isPending}
            onCancelPlan={steps.cancelPlan}
            actionPending={actionPending}
            actionFeedbackPending={actionFeedbackPending}
            stepActionsPending={stepActionsPending}
            contentBlocking={contentBlocking}
            shopChanging={shopChanging}
            revisionConflict={revisionConflict}
            nextDisabled={
              stepActionsPending ||
              publicationPending ||
              shopChanging ||
              revisionConflict ||
              requiredDataUnavailable ||
              (canEdit &&
                currentStep === "products" &&
                (!draft?.products.length || stepInputs.catalogBlocked)) ||
              (canEdit && objectiveInputsIncomplete) ||
              structureEvaluationBlocksContinue
            }
            onBack={steps.goBack}
            onNext={!canEdit ? onClose : steps.goNext}
          />
        ) : null}
      </div>

      <ReviewStrategyDialog
        open={reviewStrategyEditing}
        onOpenChange={setReviewStrategyEditing}
        locked={actionPending || strategySubviewDirty}
        plan={plan}
        capabilities={capabilitiesQuery.data}
        strategyEditorEpoch={strategyEditorEpoch}
        mutationPending={actionSerializing}
        recommendationPending={mutations.strategyRecommendationMutation.isPending}
        onApplyOperations={preview.applyStrategyOperations}
        onAcceptRecommendation={preview.acceptStrategyRecommendation}
        onRefreshRecommendation={preview.refreshStrategyRecommendation}
        onLocalDirtyChange={session.handleStrategySubviewDirtyChange}
      />

      <PreviewRegenerationDialog
        open={regeneration.regenerateDialogOpen}
        onSettle={regeneration.settlePreviewRegeneration}
      />

      <DiscardChangesDialog
        open={exitGuard.closeDialogOpen}
        onOpenChange={exitGuard.changeCloseDialogOpen}
        actionPending={actionPending}
        onDiscard={exitGuard.discardChangesAndClose}
      />
    </>
  );
}
